import React, { useRef, useState } from 'react';
import { DownloadIcon, UploadIcon, PlusIcon, Trash2Icon, HistoryIcon, LockIcon } from 'lucide-react';
import { LookupTable } from '../../data/blcTables';
import { csvToRows, downloadCsv, gridToCsv, tableRange } from '../../utils/blcCalculation';
interface LookupTableEditorProps {
  table: LookupTable;
  onChange: (table: LookupTable) => void;
}
export function LookupTableEditor({
  table,
  onChange
}: LookupTableEditorProps) {
  const [locked, setLocked] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const colDecimals = table.colStep === 0.01 ? 2 : 1;
  const rowDecimals = table.colStep === 0.01 ? 2 : 0;
  const stamp = (rows: LookupTable['rows']): void => {
    onChange({
      ...table,
      rows,
      updatedBy: 'You (current session)',
      updatedAt: new Date().toISOString().slice(0, 16).replace('T', ' ')
    });
  };
  const updateCell = (rowIdx: number, colIdx: number, raw: string): void => {
    const parsed = parseFloat(raw);
    const rows = table.rows.map((row, i) => i === rowIdx ? {
      ...row,
      values: row.values.map((v, j) => j === colIdx ? Number.isFinite(parsed) ? parsed : 0 : v)
    } : row);
    stamp(rows);
  };
  const addRow = (): void => {
    const last = table.rows[table.rows.length - 1];
    const nextBase = last ? Number((last.base + (table.colStep === 0.01 ? 0.1 : 1)).toFixed(2)) : 1;
    stamp([...table.rows, {
      base: nextBase,
      values: Array.from({
        length: 10
      }, () => 0)
    }]);
  };
  const removeRow = (rowIdx: number): void => {
    stamp(table.rows.filter((_, i) => i !== rowIdx));
  };
  const handleImport = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const rows = csvToRows(String(reader.result || ''));
      if (rows.length > 0) {
        stamp(rows);
      }
    };
    reader.readAsText(file);
    event.target.value = '';
  };
  return <section className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-150" data-mp="3399uzot6f0y9">
      <header className="p-5 border-b border-gray-200 flex flex-wrap items-start justify-between gap-4" data-id="element-151" data-mp="3871mwittmn2g">
        <div data-id="element-152" data-mp="3ca7tjrkuig9g">
          <h3 className="font-semibold text-gray-900" data-id="element-153" data-mp="3carb376l32my">{table.name}</h3>
          <p className="text-sm text-gray-600 mt-1 max-w-xl" data-id="element-154" data-mp="3h7hqrteh7w7u">
            {table.description}
          </p>
          <p className="text-xs text-gray-500 mt-2" data-id="element-155" data-mp="3m6b2bga7dd5d">
            Valid range:{' '}
            <span className="font-medium text-gray-700" data-id="element-156" data-mp="3qzdnb00wze9y">
              {tableRange(table)}
            </span>{' '}
            · {table.rows.length} rows × 10 columns
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2" data-id="element-157" data-mp="3ueb376yeupr">
          <button type="button" onClick={() => setLocked(!locked)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${locked ? 'border-gray-300 text-gray-700 hover:bg-gray-50' : 'border-amber-300 bg-amber-50 text-amber-800'}`} data-id="element-158" data-mp="3rs3j7k2cf7j">
            <LockIcon size={14} data-id="element-159" data-mp="1kbwucx5gvs7u" />
            {locked ? 'Unlock to edit' : 'Editing enabled'}
          </button>
          <button type="button" onClick={() => downloadCsv(`${table.id}.csv`, gridToCsv(table))} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50" data-id="element-160" data-mp="2c18bi4th87vo">
            <DownloadIcon size={14} data-id="element-161" data-mp="236tlb77ercxs" />
            Export CSV
          </button>
          <button type="button" disabled={locked} onClick={() => fileInputRef.current?.click()} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-162" data-mp="1y9bmwar0og41">
            <UploadIcon size={14} data-id="element-163" data-mp="2fi2upkz9sod3" />
            Import CSV
          </button>
          <input ref={fileInputRef} type="file" accept=".csv,text/csv" onChange={handleImport} className="hidden" data-id="element-164" data-mp="2ffzvcac89ivp" />
        </div>
      </header>

      <div className="overflow-auto max-h-[26rem]" data-id="element-165" data-mp="26j8yvwtrk20l">
        <table className="w-full text-sm border-collapse" data-id="element-166" data-mp="2u8a317vkcr02">
          <thead className="sticky top-0 z-10 bg-gray-50" data-id="element-167" data-mp="2y8c4ph1atkfc">
            <tr data-id="element-168" data-mp="2y7slzy3tqs09">
              <th className="sticky left-0 z-20 bg-gray-50 px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-r border-gray-200" data-id="element-169" data-mp="2yaesduezb1gw">
                {table.rowLabel}
              </th>
              {Array.from({
              length: 10
            }, (_, i) => <th key={i} className="px-2 py-2 text-center text-xs font-semibold text-gray-600 border-b border-gray-200 min-w-[4.5rem]" data-id="element-170" data-mp="3d4jls8heu5qe">
                  {(i * table.colStep).toFixed(colDecimals)}
                </th>)}
              <th className="px-2 py-2 border-b border-gray-200" data-id="element-171" data-mp="3n9jalt2kb0w3" />
            </tr>
          </thead>
          <tbody data-id="element-172" data-mp="4v0mqfdynnd1">
            {table.rows.map((row, rowIdx) => <tr key={`${row.base}-${rowIdx}`} className="even:bg-gray-50/60" data-id="element-173" data-mp="3w34pdceq8i5i" data-mpi={rowIdx}>
                <th className="sticky left-0 z-10 bg-white even:bg-gray-50 px-3 py-1.5 text-left font-semibold text-gray-900 border-r border-gray-200 whitespace-nowrap" data-id="element-174" data-mp="38bz9ix8sdpv7">
                  {row.base.toFixed(rowDecimals)}
                </th>
                {row.values.map((value, colIdx) => <td key={colIdx} className="px-1 py-1" data-id="element-175" data-mp="3dbvht8oio3gk" data-mpi={colIdx}>
                    <input type="number" step={table.decimals === 0 ? 1 : 0.0001} value={value} readOnly={locked} onChange={e => updateCell(rowIdx, colIdx, e.target.value)} aria-label={`${table.rowLabel} ${row.base} + ${(colIdx * table.colStep).toFixed(colDecimals)}`} className={`w-full px-2 py-1 text-right rounded border text-xs tabular-nums ${locked ? 'border-transparent bg-transparent text-gray-700' : 'border-gray-200 bg-white text-gray-900 focus:ring-2 focus:ring-[#1B5E20] focus:outline-none'}`} data-id="element-176" data-mp="3daamvhyaiii6" />
                  </td>)}
                <td className="px-2 py-1" data-id="element-177" data-mp="qw9a9irsux4r">
                  <button type="button" disabled={locked} onClick={() => removeRow(rowIdx)} aria-label={`Remove row ${row.base}`} className="p-1 text-gray-400 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-178" data-mp="qv7vp4bkrsia">
                    <Trash2Icon size={14} data-id="element-179" data-mp="34bzzw400d5r" />
                  </button>
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>

      <footer className="px-5 py-3 border-t border-gray-200 flex flex-wrap items-center justify-between gap-3 bg-gray-50" data-id="element-180" data-mp="3mu01646ye4sw">
        <p className="text-xs text-gray-500 flex items-center gap-1.5" data-id="element-181" data-mp="3hwgm522flnzw">
          <HistoryIcon size={14} data-id="element-182" data-mp="3curbvon19ecv" />
          Last updated by{' '}
          <span className="font-medium text-gray-700" data-id="element-183" data-mp="38txocwdi61ki">{table.updatedBy}</span> on{' '}
          <span className="font-medium text-gray-700" data-id="element-184" data-mp="3438whfxwv2ku">{table.updatedAt}</span>
        </p>
        <button type="button" disabled={locked} onClick={addRow} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-[#1B5E20] text-white hover:bg-[#2E7D32] disabled:opacity-40 disabled:cursor-not-allowed" data-id="element-185" data-mp="2z3w1g7wvyp76">
          <PlusIcon size={14} data-id="element-186" data-mp="2p7j6l6plu5au" />
          Add row
        </button>
      </footer>
    </section>;
}